import React, { useEffect } from 'react';
import { Button, Card, CardContent, Input, Label, Loader, Select, Text, Title } from '@upsales/components';
import { connect } from 'react-redux';
import AdminHeader from './AdminHeader';
import AppService from 'App/legacy/AppService';
import t from 'Components/Helpers/translate';
import {
	dismissKey,
	load,
	mint,
	revoke,
	setDefaultRole,
	type ScimProvisioningState
} from 'Store/reducers/ScimProvisioningReducer';

type Props = ScimProvisioningState & {
	load: () => void;
	mint: () => void;
	revoke: () => void;
	setDefaultRole: (roleId: number) => void;
	dismissKey: () => void;
};

const mapStateToProps = (state: { ScimProvisioning: ScimProvisioningState }) => ({ ...state.ScimProvisioning });
const mapDispatchToProps = { load, mint, revoke, setDefaultRole, dismissKey };

/**
 * SCIM provisioning settings (WEB-8957).
 *
 * Three things in the order an admin does them: pick the role provisioned users
 * get, generate the credential, copy the two values into the identity provider.
 * The role comes first on screen because the credential cannot be generated
 * without it — the server refuses, since a provisioned user with no role can see
 * the whole account.
 */
export function ScimProvisioning({
	loading,
	saving,
	enabled,
	baseUrl,
	defaultRoleId,
	mintedKey,
	load,
	mint,
	revoke,
	setDefaultRole,
	dismissKey
}: Props) {
	useEffect(() => {
		load();
	}, []);

	const roles = AppService.getRoles() || [];
	const roleOptions = roles.map((role: { id: number; name: string }) => ({ id: role.id, title: role.name }));
	const selectedRole = roleOptions.find((option: { id: number }) => option.id === defaultRoleId) || null;

	return (
		// Every admin page supplies its own #admin-root: _admin.scss scopes
		// #admin-content, .admin-page-header and #admin-loader underneath it, and the
		// Angular route only renders an empty div. Without it the page loses the
		// layout entirely and the loader — hidden by `visibility: hidden` under
		// #admin-root — stays on screen permanently.
		<div id="admin-root">
			<div id="admin-page-scim">
				<AdminHeader title={t('admin.scim.title')} description={t('admin.scim.description')} image="api.svg" />

				<div id="admin-content">
					<Card space="mbxl">
						<CardContent>
							<Title size="sm">{t('admin.scim.defaultRole')}</Title>
							<Text size="sm" color="grey-11" space="mbl">
								{t('admin.scim.defaultRoleDescription')}
							</Text>
							<Label>{t('default.role')}</Label>
							<Select
								anchor={document.body}
								options={roleOptions}
								value={selectedRole}
								onChange={(option: { id: number }) => setDefaultRole(option.id)}
								onClear={undefined}
								placeholder={t('admin.scim.defaultRolePlaceholder')}
								disabled={loading}
							/>
						</CardContent>
					</Card>

					<Card space="mbxl">
						<CardContent>
							<Title size="sm">{t('admin.scim.credential')}</Title>
							<Text size="sm" color="grey-11" space="mbl">
								{t('admin.scim.credentialDescription')}
							</Text>

							{/* Shown once. There is nowhere to fetch it back from. */}
							{mintedKey ? (
								<div className="scim-minted-key">
									<Label>{t('admin.scim.tokenOnce')}</Label>
									<Input value={mintedKey} readOnly onClick={(e: any) => e.target.select()} />
									<Button type="link" onClick={dismissKey}>
										{t('admin.scim.tokenCopied')}
									</Button>
								</div>
							) : null}

							<Label>{t('admin.scim.baseUrl')}</Label>
							<Input value={baseUrl} readOnly onClick={(e: any) => e.target.select()} />

							<div className="scim-actions">
								<Button onClick={mint} disabled={saving || loading || !defaultRoleId}>
									{enabled ? t('admin.scim.regenerate') : t('admin.scim.generate')}
								</Button>
								{enabled ? (
									<Button type="link" color="red" onClick={revoke} disabled={saving}>
										{t('admin.scim.revoke')}
									</Button>
								) : null}
							</div>

							{!defaultRoleId ? (
								<Text size="sm" color="orange" space="mtm">
									{t('admin.scim.roleRequiredBeforeToken')}
								</Text>
							) : null}

							<Text size="sm" color="grey-11" space="mtl">
								{t('admin.scim.syncInterval')}
							</Text>
						</CardContent>
					</Card>
				</div>

				<div id="admin-loader" className={loading ? 'visible' : undefined}>
					<Loader size="md" className="upsales-loader" />
				</div>
			</div>
		</div>
	);
}

const ConnectedScimProvisioning = connect(mapStateToProps, mapDispatchToProps)(ScimProvisioning);
export default ConnectedScimProvisioning;
(window as any).ScimProvisioning = ConnectedScimProvisioning;
